import { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Package, Plus, Minus, Mic, Send, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { executeWithRetry, supabase } from '@/lib/supabase';
import { withTimeout } from '@/lib/with-timeout';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

interface InventoryItem {
  id: string;
  item_name?: string;
  name?: string; // Support both formats (from DB: item_name, from props: name)
  unit: string;
}

interface InventoryOrderSectionProps {
  inventory: InventoryItem[];
  locationId: string;
  orgId: string | null;
  disabled?: boolean;
}

interface OrderQuantity {
  itemId: string;
  quantity: number;
}

export const InventoryOrderSection = ({ inventory: propInventory, locationId, orgId, disabled = false }: InventoryOrderSectionProps) => {
  const queryClient = useQueryClient();
  const [orderQuantities, setOrderQuantities] = useState<Map<string, number>>(new Map());
  const [orderNotes, setOrderNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const lastResultIndexRef = useRef<number>(0);

  const { data: products = [], isPending: productsPending } = useQuery({
    queryKey: ['cleaner-location-inventory-products', locationId, orgId],
    enabled: Boolean(locationId && locationId !== 'undefined' && orgId),
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<InventoryItem[]> => {
      console.log('[InventoryOrderSection] Pobieranie produktów dla locationId:', locationId, 'orgId:', orgId);
      type Row = { id: string; item_name?: string; unit?: string | null; cleaning_catalog?: { name?: string; unit?: string } | null };
      const toResolved = (row: Row): InventoryItem => ({
        id: row.id,
        item_name: (row.cleaning_catalog?.name ?? row.item_name) || '',
        name: (row.cleaning_catalog?.name ?? row.item_name) || '',
        unit: row.cleaning_catalog?.unit ?? row.unit ?? 'szt',
      });

      const [locationRes, symbolRes] = await Promise.all([
        supabase
          .from('cleaning_inventory')
          .select('*, cleaning_catalog(name, unit)')
          .eq('location_id', locationId),
        supabase
          .from('cleaning_inventory')
          .select('*, cleaning_catalog(name, unit)')
          .is('location_id', null)
          .eq('org_id', orgId as string),
      ]);

      if (locationRes.error && symbolRes.error) {
        console.warn('[InventoryOrderSection] Używam fallback (propInventory) z powodu błędów');
        return propInventory || [];
      }

      const byName = new Map<string, InventoryItem>();
      for (const item of symbolRes.data || []) {
        const resolved = toResolved(item as Row);
        byName.set(resolved.item_name || resolved.name || resolved.id, resolved);
      }
      for (const item of locationRes.data || []) {
        const resolved = toResolved(item as Row);
        byName.set(resolved.item_name || resolved.name || resolved.id, resolved);
      }

      const uniqueProducts = Array.from(byName.values());
      return uniqueProducts.length > 0 ? uniqueProducts : (propInventory || []);
    },
  });

  const { data: pendingOrderItems = [] } = useQuery({
    queryKey: ['cleaner-location-inventory-pending', locationId],
    enabled: Boolean(locationId),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<Array<{ item_id?: string }>> => {
      const { data, error } = await supabase
        .from('material_requests')
        .select('items')
        .eq('location_id', locationId)
        .eq('status', 'pending');
      if (error) throw error;

      const items: Array<{ item_id?: string }> = [];
      for (const order of data || []) {
        if (!order.items) continue;
        const parsedItems = Array.isArray(order.items) ? order.items : [order.items];
        for (const item of parsedItems) {
          items.push(item as { item_id?: string });
        }
      }
      return items;
    },
  });

  const pendingItemIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of pendingOrderItems) {
      if (item.item_id) ids.add(item.item_id);
    }
    return ids;
  }, [pendingOrderItems]);

  const handleQuantityChange = (itemId: string, value: string) => {
    const numValue = parseInt(value, 10) || 0;
    if (numValue >= 0) {
      setOrderQuantities(prev => {
        const newMap = new Map(prev);
        if (numValue > 0) {
          newMap.set(itemId, numValue);
        } else {
          newMap.delete(itemId);
        }
        return newMap;
      });
    }
  };

  const handleIncreaseQuantity = (itemId: string) => {
    const current = orderQuantities.get(itemId) || 0;
    handleQuantityChange(itemId, String(current + 1));
  };

  const handleDecreaseQuantity = (itemId: string) => {
    const current = orderQuantities.get(itemId) || 0;
    if (current > 0) {
      handleQuantityChange(itemId, String(current - 1));
    }
  };

  const handleVoiceInput = () => {
    // Check if Speech Recognition is available
    const SpeechRecognition = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      toast.error('Rozpoznawanie mowy nie jest obsługiwane w tej przeglądarce');
      return;
    }

    // If already listening, stop recognition
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      lastResultIndexRef.current = 0;
      return;
    }

    // Initialize Speech Recognition
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'pl-PL'; // Polish language

    recognition.onstart = () => {
      setIsListening(true);
      lastResultIndexRef.current = 0;
      console.log('[InventoryOrderSection] Rozpoznawanie mowy rozpoczęte');
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      // Only process new final results to avoid duplicates
      const newResults: string[] = [];
      for (let i = lastResultIndexRef.current; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          newResults.push(result[0].transcript);
        }
      }
      
      if (newResults.length > 0) {
        const transcript = newResults.join(' ');
        lastResultIndexRef.current = event.results.length;
        
        setOrderNotes(prev => {
          const newText = prev ? `${prev} ${transcript}` : transcript;
          return newText.trim();
        });
        
        console.log('[InventoryOrderSection] Rozpoznany tekst:', transcript);
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error('[InventoryOrderSection] Błąd rozpoznawania mowy:', event.error);
      
      if (event.error === 'no-speech') {
        toast.info('Nie wykryto mowy. Spróbuj ponownie.');
      } else if (event.error === 'not-allowed') {
        toast.error('Brak uprawnień do korzystania z mikrofonu');
      } else {
        toast.error(`Błąd rozpoznawania mowy: ${event.error}`);
      }
      
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      lastResultIndexRef.current = 0;
      console.log('[InventoryOrderSection] Rozpoznawanie mowy zakończone');
    };

    recognitionRef.current = recognition;

    try {
      recognition.start();
    } catch (error) {
      console.error('[InventoryOrderSection] Błąd uruchomienia rozpoznawania:', error);
      toast.error('Nie udało się uruchomić rozpoznawania mowy');
      setIsListening(false);
    }
  };

  // Cleanup speech recognition on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
    };
  }, []);

  const handleSendOrder = async () => {
    if (orderQuantities.size === 0) {
      toast.error('Wybierz produkty do zamówienia');
      return;
    }

    if (!orgId || !locationId) {
      toast.error('Brak wymaganych danych (org_id, location_id)');
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user }, error: authError } = await withTimeout(
        supabase.auth.getUser(),
        8000
      );
      if (authError || !user) {
        toast.error('Błąd autentykacji. Zaloguj się ponownie.');
        return;
      }

      // Build items array: id, name, quantity (number). Also requested_qty for compatibility.
      const itemsArray = Array.from(orderQuantities.entries())
        .map(([itemId, rawQty]) => {
          const qty = Number(rawQty) || 0;
          const product = products.find(p => p.id === itemId);
          return {
            id: itemId,
            item_id: itemId,
            name: product?.item_name || product?.name || '',
            quantity: qty,
            requested_qty: qty,
            delivered_qty: 0,
            unit: product?.unit || '',
          };
        })
        .filter((item) => item.quantity > 0);

      // Create ONE record with all items in JSONB array
      const { error } = await executeWithRetry(
        async () =>
          await supabase.from('material_requests').insert({
            org_id: orgId,
            location_id: locationId,
            requester_id: user.id,
            items: itemsArray,
            notes: orderNotes || null,
            status: 'pending',
          }),
        { isReadOperation: false }
      );

      if (error) {
        console.error('[InventoryOrderSection] Błąd podczas zapisywania zamówienia:', error);
        toast.error(`Błąd zapisywania zamówienia: ${error.message}`);
      } else {
        toast.success(`Wysłano zamówienie na ${orderQuantities.size} produktów`);
        // Reset form
        setOrderQuantities(new Map());
        setOrderNotes('');
        void queryClient.invalidateQueries({ queryKey: ['cleaner-location-inventory-pending', locationId] });
      }
    } catch (error) {
      console.error('[InventoryOrderSection] Nieoczekiwany błąd:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Nieoczekiwany błąd podczas wysyłania zamówienia'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (productsPending && products.length === 0) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold">Wirtualny Magazyn</h3>
        </div>
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold">Wirtualny Magazyn</h3>
        </div>
        <p className="text-sm text-muted-foreground italic">Brak przypisanego wyposażenia</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Package className="h-5 w-5 text-muted-foreground" />
        <h3 className="font-semibold">Wirtualny Magazyn</h3>
      </div>

      {/* Product list */}
      <div className="space-y-2">
        {products.map((item) => {
          const isPending = pendingItemIds.has(item.id);
          const quantity = orderQuantities.get(item.id) || 0;
          
          return (
            <div 
              key={item.id}
              className={cn(
                "flex items-center justify-between p-3 rounded-lg bg-white dark:bg-zinc-900 shadow-sm",
                isPending && "opacity-60"
              )}
            >
              <div className="flex-1 flex items-center gap-3">
                <div className="flex-1">
                  <span className="font-medium">{item.item_name || item.name || ''}</span>
                  <span className="text-sm text-muted-foreground ml-2">({item.unit})</span>
                </div>
                {isPending && (
                  <Badge 
                    variant="secondary" 
                    className="bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400"
                  >
                    W dostawie
                  </Badge>
                )}
              </div>
              
              {!isPending && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDecreaseQuantity(item.id)}
                    disabled={isSubmitting || disabled || quantity === 0}
                    className="h-9 w-9 p-0"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    type="number"
                    min="0"
                    value={quantity || ''}
                    onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                    className="w-20 h-9 text-center"
                    placeholder="0"
                    disabled={isSubmitting || disabled}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleIncreaseQuantity(item.id)}
                    disabled={isSubmitting || disabled}
                    className="h-9 w-9 p-0"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Order notes */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground">
          Uwagi do zamówienia
        </label>
        <div className="relative">
          <Textarea
            value={orderNotes}
            onChange={(e) => setOrderNotes(e.target.value)}
            placeholder="Dodaj uwagi do zamówienia..."
            className="min-h-[80px] pr-12 resize-none bg-white dark:bg-zinc-900"
            disabled={isSubmitting || disabled}
          />
          <Button
            size="icon"
            variant="ghost"
            onClick={handleVoiceInput}
            className={cn(
              "absolute right-2 top-2 h-8 w-8",
              isListening 
                ? "text-red-500 animate-pulse" 
                : "text-muted-foreground hover:text-primary"
            )}
            disabled={isSubmitting || disabled}
          >
            <Mic className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Submit button */}
      <Button 
        className="w-full h-11 font-medium"
        onClick={handleSendOrder}
        disabled={orderQuantities.size === 0 || isSubmitting || disabled}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Wysyłanie...
          </>
        ) : (
          <>
            <Send className="mr-2 h-4 w-4" />
            Zamów ({orderQuantities.size})
          </>
        )}
      </Button>
    </div>
  );
};
